import { Panel, PanelProps } from '../Panel/Panel'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight } from '@/app/icons/ArrowRight'

type CollapsiblePanelProps = React.PropsWithChildren<
  {
    header: React.ReactNode
    defaultOpen?: boolean
    noToggle?: boolean
    summaryProps?: React.DetailsHTMLAttributes<HTMLDivElement>
  } & PanelProps
>

export function CollapsiblePanel({
  children,
  header,
  defaultOpen = false,
  noToggle = false,
  summaryProps,
  ...props
}: CollapsiblePanelProps) {
  const detailsElRef = useRef<HTMLDetailsElement | null>(null)
  const [isOpen, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (!detailsElRef.current) return

    const detailRef = detailsElRef.current

    const handleToggle = ({ currentTarget }) => {
      setOpen(currentTarget.open ?? false)
    }

    detailRef.addEventListener?.('toggle', handleToggle)

    return () => {
      detailRef.removeEventListener?.('toggle', handleToggle)
    }
  }, [detailsElRef, setOpen])

  return (
    <Panel
      {...props}
      className={`cursor-pointer focus-within:outline group flex flex-row !p-4 !md:p6 text-mydra-black hover:bg-mydra-gray hover:shadow-course-content-item transition-shadow ${isOpen ? 'h-full' : ''} ${props.className ?? ''}`}
      onClick={(e) => {
        if (e.currentTarget !== e.target) {
          return
        }
        setOpen((v) => !v)
      }}
    >
      <details className="w-full" ref={detailsElRef} open={isOpen}>
        <summary
          {...summaryProps}
          className={`font-semibold list-none focus:outline-none ${summaryProps} [&::-webkit-details-marker]:hidden`}
        >
          {header}
        </summary>
        <div
          className={`text-gray-light ${isOpen ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            if (isOpen) {
              setOpen(false)
            }
          }}
        >
          {children}
        </div>
      </details>
      {!noToggle && (
        <button
          className="p-2 ml-auto transition-transform ease-in appearance-none focus:outline-none shrink-0 group-hover:rotate-180"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpen((isOpen) => !isOpen)
          }}
        >
          <ArrowRight
            className={`w-2 md:w-3 text-[#000] ${isOpen ? 'rotate-90' : '-rotate-90'}`}
          />
        </button>
      )}
    </Panel>
  )
}
